import React, { useEffect, useState } from 'react';
import { useGame } from '../context/GameContext';
import './Dashboard.css';

const TOPIC_META = {
  hardware:   { icon: '▣', color: '#4f6ef7' },
  os:         { icon: '◎', color: '#8b5cf6' },
  networking: { icon: '◈', color: '#0ea5e9' },
  security:   { icon: '◆', color: '#10b981' },
  software:   { icon: '▤', color: '#f59e0b' },
};

export default function Dashboard({ navigate }) {
  const {
    username, level, xp, xpProgress, xpForNextLevel,
    completedTopics, unlockedAchievements, quizHistory,
    totalCorrect, totalAnswered, streak,
  } = useGame();

  const [topics, setTopics] = useState([]);
  const xpPercent = Math.min(100, (xpProgress / xpForNextLevel) * 100);
  const accuracy = totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;

  useEffect(() => {
    fetch('/api/topics')
      .then(r => r.json())
      .then(setTopics)
      .catch(() => {});
  }, []);

  const recentQuizzes = quizHistory.slice(0, 3);

  return (
    <div className="dashboard">
      {/* Hero player card */}
      <div className="dashboard-hero card">
        <div className="hero-left">
          <div className="hero-avatar">{username[0]?.toUpperCase()}</div>
          <div className="hero-info">
            <h1 className="hero-username">{username}</h1>
            <div className="hero-level-badge">
              <span className="level-icon">▲</span>
              Level {level} Explorer
            </div>
          </div>
        </div>
        <div className="hero-xp">
          <div className="hero-xp-labels">
            <span>{xpProgress} / {xpForNextLevel} XP</span>
            <span className="xp-to-next">to Level {level + 1}</span>
          </div>
          <div className="xp-bar-outer">
            <div className="xp-bar-inner" style={{ width: `${xpPercent}%` }} />
          </div>
          <div className="hero-total-xp">{xp} Total XP</div>
        </div>
      </div>

      {/* Stats row */}
      <div className="stats-row">
        <div className="stat-card card">
          <div className="stat-value" style={{ color: 'var(--accent-blue)' }}>{completedTopics.length}<span className="stat-denom">/5</span></div>
          <div className="stat-label">Topics Completed</div>
        </div>
        <div className="stat-card card">
          <div className="stat-value" style={{ color: 'var(--accent-green)' }}>{accuracy}<span className="stat-denom">%</span></div>
          <div className="stat-label">Accuracy</div>
        </div>
        <div className="stat-card card">
          <div className="stat-value" style={{ color: 'var(--accent-gold)' }}>{unlockedAchievements.length}</div>
          <div className="stat-label">Achievements</div>
        </div>
        <div className="stat-card card">
          <div className="stat-value" style={{ color: 'var(--accent-red)' }}>{quizHistory.length}</div>
          <div className="stat-label">Quizzes Taken</div>
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Topic progress */}
        <div className="dashboard-section">
          <div className="section-header">
            <div>
              <h2 className="section-title">Topic Progress</h2>
              <p className="section-subtitle">Your learning journey</p>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('lessons')}>
              View All
            </button>
          </div>
          <div className="topic-progress-list">
            {topics.map(t => {
              const meta = TOPIC_META[t.id] || {};
              const done = completedTopics.includes(t.id);
              const relevantQuizzes = quizHistory.filter(h => h.topicId === t.id);
              const best = relevantQuizzes.length > 0 ? Math.max(...relevantQuizzes.map(h => h.percentage)) : 0;
              return (
                <div
                  key={t.id}
                  className="topic-progress-item card card-clickable"
                  onClick={() => navigate('lesson-detail', t)}
                  style={{ '--topic-color': meta.color }}
                >
                  <div className="tp-icon" style={{ background: `${meta.color}22`, color: meta.color }}>
                    {meta.icon}
                  </div>
                  <div className="tp-info">
                    <div className="tp-name">{t.title}</div>
                    <div className="tp-bar-outer">
                      <div className="tp-bar-inner" style={{ width: `${best}%`, background: meta.color }} />
                    </div>
                  </div>
                  <div className="tp-right">
                    {done ? (
                      <span className="tp-done">✓</span>
                    ) : (
                      <span className="tp-score">{Math.round(best)}%</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent activity + CTA */}
        <div className="dashboard-aside">
          <div className="card cta-card">
            <h3 className="cta-title">Ready to Level Up?</h3>
            <p className="cta-text">Pick a topic, study the material, then take the quiz to earn XP.</p>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => navigate('lessons')}>
              Start Learning
            </button>
          </div>

          {recentQuizzes.length > 0 && (
            <div className="card">
              <h3 className="section-title" style={{ fontSize: '1rem', marginBottom: 16 }}>Recent Quizzes</h3>
              <div className="recent-list">
                {recentQuizzes.map((q, i) => {
                  const meta = TOPIC_META[q.topicId] || {};
                  return (
                    <div key={i} className="recent-item">
                      <span className="recent-icon" style={{ color: meta.color }}>{meta.icon}</span>
                      <div className="recent-info">
                        <div className="recent-topic">{q.topicId}</div>
                        <div className="recent-score">{q.correct}/{q.total} correct</div>
                      </div>
                      <span className="recent-pct" style={{ color: q.percentage >= 80 ? 'var(--accent-green)' : q.percentage >= 60 ? 'var(--accent-gold)' : 'var(--accent-red)' }}>
                        {Math.round(q.percentage)}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
